import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'sepia';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem('april-ai-theme');
    if (saved === 'light' || saved === 'dark' || saved === 'sepia') return saved;
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('april-ai-theme', theme);
    const root = document.documentElement;
    root.classList.remove('dark', 'sepia');
    if (theme === 'dark') root.classList.add('dark');
    else if (theme === 'sepia') root.classList.add('sepia');
  }, [theme]);

  const toggleTheme = () => {
    // Cycle: light -> dark -> sepia -> light
    setThemeState(prev => prev === 'light' ? 'dark' : prev === 'dark' ? 'sepia' : 'light');
  };

  const setTheme = (t: Theme) => setThemeState(t);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
